import { getId } from 'services/utils'
import { WithId, WithIdAndName } from 'types/common'
import { ActivityStatus } from 'types/form'

export type ProductPreset = WithIdAndName

export type PackageBase = WithIdAndName & {
  description: string
  status: ActivityStatus
}

export type ProductPackage<T extends ProductPreset = ProductPreset> =
  PackageBase & {
    products: T[]
    isBundledPackage: boolean
  }

export type ProductPresetWithUpselling = BaseProductPreset & {
  upselling?: BaseProductPreset[]
}

export type BaseProductPreset = ProductPreset & { quantityMultiplier: number }

export type BaseProductPackage = ProductPackage<BaseProductPreset>

export type APIBaseProductPackage = BaseProductPackage

export type OptionGroupPreset = WithId & WithItems<number>

export type OptionedPreset = ProductPresetWithUpselling & {
  options: OptionGroupPreset[]
  optionsFull: (ProductPreset & WithItems<ProductPreset>)[]
}

export type WithItems<T extends WithId | number> = { items: T[] }

export type APIOptionedPreset = Omit<OptionedPreset, 'options'> & {
  options: (ProductPreset & WithItems<ProductPreset>)[]
}

export type APIOptionedPackage = ProductPackage<APIOptionedPreset>

export type OptionedPackage = ProductPackage<OptionedPreset>

export const optionedPresetParse = <T extends APIOptionedPreset>(
  apiPreset: T
): OptionedPreset => ({
  ...apiPreset,
  options: apiPreset.options.map(({ items, id }) => ({
    id,
    items: items.map(getId),
  })),
  optionsFull: apiPreset.options,
})

export const optionedPackageWrite = <T extends OptionedPackage>({
  products,
  ...rest
}: T): T =>
  ({
    ...rest,
    products: products.map((p) => ({
      ...p,
      upselling: p.upselling ?? [],
    })),
  } as unknown as T)
